import React, { useEffect } from 'react'
import { Box, Typography, Button, IconButton, Radio, Alert, Checkbox } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import {
  DEFAULT_DARK_MESSAGE_TEXT_COLOR,
  DEFAULT_DARK_TITLE_TEXT_COLOR,
  DEFAULT_LIGHT_THEME_CARD_BACKGROUND,
  DEFAULT_LIGHT_THEME_CHECKED_COLOR
} from '../../../../../common/constants/themeConstants'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

export default function QuizPreview({
  data,
  completedModules,
  isCurrentThemeLight,
  selectedOptionId,
  selectedOptionIds,
  isAnswered,
  handleOptionSelect,
  handleMultipleOptionSelect,
  handleMultipleSubmit,
  handleReset
}) {
  const preselectedAnswers = completedModules
    ? completedModules.find(el => el.sequenceNumber == data.sequenceNumber).answers
    : null

  useEffect(() => {
    {
      preselectedAnswers?.length && handleOptionSelect(preselectedAnswers[0])
    }
  }, [])

  const options = data.answers || []
  const sortedOptions = [...options].sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0))
  const isMultipleChoice = options.filter(opt => opt.correct).length > 1
  const alignment = data.alignment || 'LEFT'
  const styles = getStyles(isCurrentThemeLight)

  return (
    <Box sx={styles.previewContainer}>
      <Typography
        sx={{
          ...styles.previewTitle,
          textAlign: alignment
        }}
      >
        {data.context || 'Quiz Question'}
      </Typography>

      {isAnswered && !!data.comment && (
        <Alert
          severity='info'
          iconMapping={{ warning: <InfoOutlinedIcon fontSize='inherit' /> }}
          sx={{
            ...styles.infoCommentAlert,
            width: 'fit-content',
            alignSelf: alignment === 'CENTER' ? 'center' : alignment === 'RIGHT' ? 'flex-end' : 'flex-start'
          }}
          action={
            <IconButton size='small' onClick={() => handleReset()}>
              <CloseIcon fontSize='small' />
            </IconButton>
          }
        >
          {data.comment}
        </Alert>
      )}

      <Box
        sx={{
          ...styles.optionsPreviewContainer,
          alignItems: alignment === 'CENTER' ? 'center' : alignment === 'RIGHT' ? 'flex-end' : 'flex-start'
        }}
      >
        {sortedOptions.map(option => {
          const isSelected = isMultipleChoice
            ? (preselectedAnswers || selectedOptionIds).includes(option.text)
            : (preselectedAnswers?.length ? preselectedAnswers[0] : selectedOptionId) === option.text
          const showCorrectIcon = isAnswered && option.correct
          const showIncorrectIcon = isAnswered && isSelected && !option.correct

          return (
            <Box
              key={option.sequenceNumber}
              sx={{
                ...styles.optionPreviewItem,
                width: '400px',
                ...(isSelected && styles.selectedOption),
                ...(showCorrectIcon && isAnswered && styles.correctOption),
                ...(showIncorrectIcon && styles.incorrectOption)
              }}
              onClick={() =>
                isMultipleChoice
                  ? handleMultipleOptionSelect(option.text)
                  : handleOptionSelect(option.text, option.correct)
              }
            >
              {isMultipleChoice ? (
                <Checkbox checked={isSelected} sx={styles.optionCheckbox} disabled={isAnswered && !isSelected} />
              ) : (
                <Radio checked={isSelected} sx={styles.optionRadio} disabled={isAnswered && !isSelected} />
              )}
              <Typography sx={styles.optionPreviewText}>{option.text}</Typography>
              {showCorrectIcon && isAnswered && <CheckIcon color='success' />}
              {showIncorrectIcon && <CloseIcon color='error' />}
            </Box>
          )
        })}

        {isMultipleChoice && !isAnswered && (
          <Button
            variant='contained'
            sx={{
              ...styles.submitButton,
              alignSelf: alignment === 'CENTER' ? 'center' : alignment === 'RIGHT' ? 'flex-end' : 'flex-start',
              width: '400px'
            }}
            onClick={() => handleMultipleSubmit(sortedOptions)}
            disabled={selectedOptionIds.length === 0}
          >
            Submit Answer
          </Button>
        )}
      </Box>
    </Box>
  )
}

const getStyles = isCurrentThemeLight => ({
  previewContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    padding: '24px',
    height: '100%'
  },
  previewTitle: {
    fontFamily: 'DM Sans',
    fontSize: '42px',
    fontWeight: 900,
    color: isCurrentThemeLight ? DEFAULT_DARK_TITLE_TEXT_COLOR : 'rgba(255, 255, 255, 1)',
    marginBottom: '24px'
  },
  optionsPreviewContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '8px',
    paddingBottom: '2px'
  },
  optionPreviewItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    borderRadius: '8px',
    backgroundColor: isCurrentThemeLight ? DEFAULT_LIGHT_THEME_CARD_BACKGROUND : 'rgba(255, 255, 255, 0.1)',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    border: isCurrentThemeLight ? '1px solid rgba(0, 0, 0, 0.15)' : '1px solid rgba(255, 255, 255, 0.2)',
    '&:hover': {
      backgroundColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.2)'
    }
  },
  selectedOption: {
    backgroundColor: 'rgba(65, 105, 225, 0.2) !important',
    border: '1px solid rgba(65, 105, 225, 0.5)'
  },
  correctOption: {
    backgroundColor: 'rgba(76, 175, 80, 0.2) !important',
    border: '1px solid rgba(76, 175, 80, 0.5)'
  },
  incorrectOption: {
    backgroundColor: 'rgba(244, 67, 54, 0.2) !important',
    border: '1px solid rgba(244, 67, 54, 0.5)'
  },
  optionPreviewText: {
    fontFamily: 'Satoshi',
    fontSize: '16px',
    fontWeight: 400,
    color: isCurrentThemeLight ? DEFAULT_DARK_MESSAGE_TEXT_COLOR : 'rgba(255, 255, 255, 0.9)',
    flex: 1
  },
  optionRadio: {
    color: isCurrentThemeLight ? '#888' : 'rgba(208, 208, 216, 1)',
    padding: '4px',
    marginRight: '8px',
    '&.Mui-checked': {
      color: isCurrentThemeLight ? DEFAULT_LIGHT_THEME_CHECKED_COLOR : 'rgba(2, 70, 248, 1)'
    }
  },
  optionCheckbox: {
    color: isCurrentThemeLight ? '#888' : 'rgba(208, 208, 216, 1)',
    padding: '4px',
    marginRight: '8px',
    '&.Mui-checked': {
      color: isCurrentThemeLight ? DEFAULT_LIGHT_THEME_CHECKED_COLOR : 'rgba(2, 70, 248, 1)'
    }
  },
  infoCommentAlert: {
    borderRadius: '8px',
    backgroundColor: isCurrentThemeLight ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.2) !important',
    border: '1px solid rgba(0, 0, 0, 0.15)',
    color: isCurrentThemeLight ? DEFAULT_DARK_MESSAGE_TEXT_COLOR : '#fff',
    minWidth: '400px',
    '.MuiAlert-icon': {
      color: '#1976d2'
    }
  },
  submitButton: {
    marginTop: '16px',
    marginBottom: '16px',
    borderRadius: '8px',
    padding: '8px 16px',
    backgroundColor: isCurrentThemeLight ? '#1976d2' : 'rgba(25, 118, 210, 0.8)',
    '&:hover': {
      backgroundColor: isCurrentThemeLight ? '#1565c0' : 'rgba(25, 118, 210, 1)'
    },
    '&:disabled': {
      backgroundColor: isCurrentThemeLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.2)'
    },
    fontFamily: 'DM Sans, sans-serif',
    textTransform: 'none'
  }
})
